import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { Button } from '@clay-run/glaze/button';
// `/toast` has no flat `dist/toast.d.ts` — see index.tsx's import comment.
import { addApiErrorToast } from '../../_shared/api-error-toast';
import { Text } from '@clay-run/glaze/text';
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogActions,
} from '@clay-run/glaze/alert-dialog';
import { useScanApi } from '../hooks/useScanApi';
import { useCompleteIntake } from '../hooks/useCompleteIntake';
import { runPhotoIntake } from '../photo-intake';
import { decodeQrFromPhoto } from '../qr-decode';
import { preparePhotoForExtraction } from '../photo-resize';
import { useLeadScannerStore } from '../store';

/**
 * "Scan badges" — the real photo intake (U5, R8 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md), replacing U4's
 * placeholder that minted two fake filenames.
 *
 * This component is deliberately thin: it owns the picker element and the
 * React/HTTP wiring, and hands every decision to `runPhotoIntake`
 * (`../photo-intake.ts`), which is where the sequencing and QR-vs-OCR
 * routing live and where they're unit-tested.
 *
 * ─── The picker ───────────────────────────────────────────────────────────
 * `<input type="file" multiple accept="image/*">` with **no `capture`
 * attribute** (Key Technical Decision 2). `capture="environment"` biases
 * mobile browsers toward a single-shot camera and is known to fight
 * `multiple` on iOS Safari — it would break the bulk multi-select this whole
 * feature is built around. Without it the OS picker natively offers
 * camera-or-library, which is what a rep on an event floor actually wants.
 *
 * "Nothing is parsed until confirm" is satisfied by the platform, not by a
 * second confirmation step of our own — for a SINGLE photo. The native
 * picker's own Done/Add button IS the confirm, and `change` only fires
 * after the rep has committed their selection; adding an in-app confirm
 * sheet on top of a one-photo pick would be a second tap for no information
 * gain (the prototype's picker sheet was explicitly a stand-in for the OS
 * picker we can't render in static HTML — see its DEV NOTE at
 * docs/lead-scanner-prototype.html ~line 802).
 *
 * For a MULTI-photo pick (2+), that rationale doesn't hold: the OS picker's
 * Done button confirms "these are the files I selected," not "I understand
 * each photo becomes its own separate lead" — a real misconception worth
 * guarding against (U13, R15, followups §9). So 2+ photos route through an
 * in-app `AlertDialog` confirm ("Scan {n} leads") before `runPhotoIntake`
 * runs; a single photo still bypasses it entirely.
 */

/**
 * The one thing another surface needs from this component: open its picker.
 *
 * Exists for the QR scanner's camera-unavailable fallback (U12) — "take a
 * photo of the QR instead" has to land in THIS picker, because the
 * still-photo pipeline behind it is what already QR-decodes a photo (U5).
 * An imperative handle rather than a duplicated `<input type="file">` in the
 * scanner: two pickers feeding two copies of the intake wiring is exactly
 * the drift this app's single-creation-seam contract exists to prevent.
 */
export interface PhotoIntakeHandle {
  openPicker: () => void;
}

interface PhotoIntakeProps {
  campaignId: string | null;
  scannedByEmail: string | null;
  disabled?: boolean;
  /** React 19 passes `ref` as a plain prop to function components — no
   *  `forwardRef` wrapper needed. */
  ref?: Ref<PhotoIntakeHandle>;
}

export default function PhotoIntake({ campaignId, scannedByEmail, disabled, ref }: PhotoIntakeProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isIntaking, setIsIntaking] = useState(false);
  // U13 (R15): a 2+-photo pick is stashed here while the "Scan N leads"
  // confirm is open. `null` means no confirm is pending — a single-photo
  // pick never touches this state at all (it runs immediately).
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  // Round-2 live-QA bug 5: this is the one dialog in the app where Enter
  // should trigger the primary action rather than Cancel — see the effect
  // below.
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Round-2 live-QA bug 5: with `AlertDialogHeader`'s `onClose` (and its X
  // close button) removed below, Base UI's dialog moves initial focus to the
  // first tabbable element in the popup, which is now Cancel — so Enter
  // would dismiss this non-destructive "Scan N leads?" confirm instead of
  // running the scan. Every OTHER dialog in this app is a destructive
  // confirm where Enter-defaults-to-Cancel is the correct, intentional
  // behavior (do not copy this effect there); this one is the exception.
  // Base UI's own initial-focus effect runs after children's effects, so an
  // immediate `.focus()` call here would just get overridden by it — the
  // short delay lets Base UI's focus land first, then this one wins.
  // Idempotent under StrictMode's setup→cleanup→setup: the cleanup always
  // clears whatever timeout the previous invocation scheduled.
  useEffect(() => {
    if (pendingFiles === null) return;
    const timeout = setTimeout(() => {
      confirmButtonRef.current?.focus();
    }, 50);
    return () => clearTimeout(timeout);
  }, [pendingFiles]);

  const { createScan, patchScan, extractBadgeFromPhoto, invalidateScans } = useScanApi();
  const completeIntake = useCompleteIntake();
  const setIntakeBatch = useLeadScannerStore((s) => s.setIntakeBatch);

  const ready = campaignId != null && scannedByEmail != null && !disabled;

  useImperativeHandle(ref, () => ({ openPicker: () => inputRef.current?.click() }), []);

  async function runIntake(files: File[]) {
    if (!campaignId || !scannedByEmail || files.length === 0) return;

    setIsIntaking(true);
    try {
      const { outcomes } = await runPhotoIntake(
        { files, campaignId, scannedByEmail },
        {
          newIdempotencyKey: () => crypto.randomUUID(),
          createProcessingScan: (input) => createScan(input),
          patchScan,
          // U2 (R2), extended by the 2026-09-18 HEIC auto-convert plan:
          // downscale-and-normalize-to-JPEG before OCR extraction only. The
          // QR decode below reads `file` UNTOUCHED — downscaling/re-encoding
          // destroys the QR module pattern (qr-decode.ts's own header
          // comment), and zxing decodes raw bytes in wasm regardless
          // (qr-decode.ts:201), so it couldn't read HEIC even if it received
          // the converted file.
          extractBadgeFromPhoto: async (scanId, file) =>
            extractBadgeFromPhoto(scanId, await preparePhotoForExtraction(file)),
          decodeQr: decodeQrFromPhoto,
          onBatchCreated: (ids) => {
            // The picker is free again as soon as the rows EXIST — not when
            // extraction finishes. A rep working a booth queue should be able
            // to shoot the next badge while the last batch is still resolving
            // in the background; the drafts list and its ProgressBar are what
            // report that ongoing work.
            setIsIntaking(false);
            if (ids.length === 0) return;
            // Order matters: register the batch BEFORE routing, so if
            // `routeAfterIntake` sends a single photo straight to its review
            // screen (unmounting Home on a narrow viewport) the progress
            // state is already in the store rather than in a dead component.
            // `completeIntake` also records the batch on a 'multi' result —
            // this call is what covers the single-photo case too (and is
            // idempotent with that one on a multi batch).
            setIntakeBatch(ids);
            completeIntake(ids);
          },
          // Each resolved photo refreshes the list immediately instead of
          // waiting up to a full poll tick — the single-photo case (straight
          // to the review screen) is the one that feels slow otherwise.
          onPhotoSettled: () => invalidateScans(),
        },
      );

      // Per-photo failures are reported once for the batch, not once per
      // photo — 14 stacked error toasts is not a usable failure mode. The
      // rows themselves already carry the detail (each failed photo's row is
      // flipped to `error` with a readable message by the pipeline).
      const failed = outcomes.filter((o) => o.leg === 'failed');
      if (failed.length > 0) {
        addApiErrorToast({
          content:
            failed.length === 1
              ? `Couldn't process ${failed[0].photoFilename} — add that lead by hand.`
              : `Couldn't process ${failed.length} of ${outcomes.length} photos — add those leads by hand.`,
          error: failed[0].error,
        });
      }
    } finally {
      // Belt-and-braces: `onBatchCreated` already cleared this. This covers
      // the only path that skips it — `runPhotoIntake` rejecting outright,
      // which it is written never to do.
      setIsIntaking(false);
    }
  }

  // U13 (R15): the picker's `onChange` handler always calls this, never
  // `runIntake` directly. A single photo bypasses the confirm entirely
  // (zero-extra-tap flow, unchanged); 2+ photos stash the selection and
  // open the "Scan N leads" `AlertDialog` below instead of running
  // immediately.
  function handleFilesPicked(files: File[]) {
    if (files.length === 0) return;
    if (files.length >= 2) {
      setPendingFiles(files);
      return;
    }
    void runIntake(files);
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
        // Not user-visible (the Button below is the affordance), but the
        // input is still the real focusable control — name it for anyone
        // reaching it via assistive tech or a test.
        aria-label="Choose badge photos"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          // Reset first: without this, picking the SAME file again fires no
          // `change` event (the value is unchanged), which reads as a dead
          // button when a rep retries a photo that failed.
          e.target.value = '';
          handleFilesPicked(files);
        }}
      />
      <Button
        variant="primary"
        size="lg"
        disabled={!ready}
        loading={isIntaking}
        onClick={() => inputRef.current?.click()}
      >
        Scan badges
      </Button>
      <AlertDialog open={pendingFiles !== null} onOpenChange={(open) => !open && setPendingFiles(null)}>
        {/* No `onClose` (round-2 live-QA bug 5) — see the `confirmButtonRef`
            effect above for why this dialog moves focus onto the primary
            action instead of leaving it on the first tabbable element. */}
        <AlertDialogHeader>Scan these badges?</AlertDialogHeader>
        <AlertDialogBody>
          <Text size="sm">
            You selected {pendingFiles?.length ?? 0} photos. Each photo becomes its own lead — they
            won't be merged into one.
          </Text>
        </AlertDialogBody>
        <AlertDialogActions>
          <Button variant="ghost" onClick={() => setPendingFiles(null)}>
            Cancel
          </Button>
          <Button
            ref={confirmButtonRef}
            variant="primary"
            onClick={() => {
              const files = pendingFiles ?? [];
              setPendingFiles(null);
              void runIntake(files);
            }}
          >
            Scan {pendingFiles?.length ?? 0} leads
          </Button>
        </AlertDialogActions>
      </AlertDialog>
    </>
  );
}
